import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  { id: "overview", title: "Overview" },
  { id: "accounts", title: "User Accounts" },
  { id: "content", title: "Ideas, Projects & Comments" },
  { id: "operational", title: "Operational Data" },
  { id: "security", title: "Auth & Security Data" },
  { id: "holds", title: "Legal & Compliance Holds" },
  { id: "request", title: "How to Request Deletion" },
  { id: "aggregated", title: "Aggregated & Anonymized Data" },
  { id: "changes", title: "Changes" },
  { id: "contact", title: "Contact" },
];

const RetentionPolicy = () => (
  <>
    <Helmet>
      <title>Records Retention & Deletion Policy — The Emerging Market</title>
      <meta
        name="description"
        content="How long The Emerging Market keeps account data, ideas, logs, and backups, and how deletion works."
      />
      <link rel="canonical" href="https://emrkt.co/retention" />
    </Helmet>
    <LegalLayout
      title="Records Retention & Deletion Policy"
      description="What we keep, how long we keep it, and how to delete your data on The Emerging Market."
      lastUpdated="June 5, 2026"
      effectiveDate="June 5, 2026"
      sections={sections}
    >
      <section id="overview">
        <h2>1. Overview</h2>
        <p>
          This policy explains how long The Emerging Market ("TEM," "we") retains different
          categories of data and how deletion works. We follow three principles: keep the
          minimum information necessary to operate the Service, give users meaningful control
          over their own content, and be transparent about the schedule.
        </p>
        <p>
          This document complements our <a href="/privacy">Privacy Policy</a>,{" "}
          <a href="/terms">Terms of Service</a>, and <a href="/dpa">Data Processing Agreement</a>. Where the two differ, the more protective
          window for the user applies.
        </p>
      </section>

      <section id="accounts">
        <h2>2. User Accounts</h2>
        <ul>
          <li><strong>Active accounts:</strong> retained for as long as the account exists.</li>
          <li>
            <strong>Inactive accounts:</strong> after <strong>36 months</strong> with no
            sign-in or qualifying activity, we send a first notification email at month 35
            and a final notice at month 36. If the account remains inactive, it is
            anonymized or deleted.
          </li>
          <li>
            <strong>"Active"</strong> means any authenticated session, saved opportunity,
            idea edit, comment, or message during the lookback window.
          </li>
        </ul>
      </section>

      <section id="content">
        <h2>3. Ideas, Projects & Comments</h2>
        <p>
          Content you submit is retained while your account is active. When you delete
          your account, you choose at deletion time between two options:
        </p>
        <ul>
          <li>
            <strong>Option A — Keep public contributions, anonymized.</strong> Your public
            ideas, public project pages, and public comments remain, attributed to{" "}
            <em>"Former member."</em> Private ideas, drafts, AI coach conversations,
            inbox messages, and saved opportunities are deleted.
          </li>
          <li>
            <strong>Option B — Delete everything.</strong> All ideas, projects, comments,
            AI conversations, and saved items are removed.
          </li>
        </ul>
        <p>
          Option A preserves value the community has already built on top of your public
          contributions (replies, citations, downstream ideas). Option B is the strongest
          privacy stance. Neither option affects content others have created in response
          to yours.
        </p>
      </section>

      <section id="operational">
        <h2>4. Operational Data</h2>
        <ul>
          <li><strong>Application & edge function logs:</strong> 90 days, then purged.</li>
          <li>
            <strong>Error monitoring (Sentry):</strong> 90 days for events; error-only
            session replays follow Sentry plan defaults.
          </li>
          <li>
            <strong>Product analytics:</strong> 14 months (aligned with the GA4 default and
            common SOC 2 baselines).
          </li>
          <li>
            <strong>Email delivery logs:</strong> retained for the life of the account.
            The unsubscribe / suppression list is kept indefinitely so we continue to honor
            opt-outs as required by CAN-SPAM.
          </li>
          <li><strong>Database backups:</strong> 30 days rolling, then overwritten.</li>
        </ul>
      </section>

      <section id="security">
        <h2>5. Authentication & Security Data</h2>
        <ul>
          <li>
            <strong>Session tokens:</strong> expire per our authentication defaults and are
            revoked on sign-out.
          </li>
          <li>
            <strong>MFA factors & recovery codes:</strong> retained while the account is
            active; deleted with the account.
          </li>
          <li><strong>Audit and admin action logs:</strong> 12 months.</li>
        </ul>
      </section>

      <section id="holds">
        <h2>6. Legal & Compliance Holds</h2>
        <p>
          Retention windows above are overridden when we have a legal obligation to
          preserve data — for example a subpoena, an ongoing dispute, or tax and finance
          records. Data subject to a hold is preserved until the obligation ends, then
          deleted according to this schedule.
        </p>
      </section>

      <section id="request">
        <h2>7. How to Request Deletion</h2>
        <ul>
          <li>
            <strong>Self-serve:</strong> visit <a href="/account">Account Settings</a> →
            Delete Account. You will be prompted to choose between Option A and Option B
            described above.
          </li>
          <li>
            <strong>Assisted:</strong> email{" "}
            <a href="mailto:info@emrkt.co">info@emrkt.co</a> and we will confirm your
            identity, action the request, and confirm completion.
          </li>
          <li>
            <strong>Timeline:</strong> we acknowledge requests within 30 days. Deletion
            from primary systems is immediate; backups are overwritten on their normal
            30-day cycle, so full removal completes within 60 days.
          </li>
        </ul>
      </section>

      <section id="aggregated">
        <h2>8. Aggregated & Anonymized Data</h2>
        <p>
          We may retain fully anonymized, aggregated metrics indefinitely for reporting and
          product improvement (for example, "X ideas submitted in Mobile County this
          quarter"). Aggregated data cannot reasonably be re-associated with you.
        </p>
      </section>

      <section id="changes">
        <h2>9. Changes to This Policy</h2>
        <p>
          Material changes to retention windows will be announced by email and via an
          in-product notice. The "Last updated" date at the top of this page reflects the
          most recent revision.
        </p>
      </section>

      <section id="contact">
        <h2>10. Contact</h2>
        <p>
          Questions about this policy or a specific record:{" "}
          <a href="mailto:info@emrkt.co">info@emrkt.co</a>.
        </p>
      </section>
    </LegalLayout>
  </>
);

export default RetentionPolicy;